import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from "recharts";
import {
  ArrowLeft, TrendingUp, TrendingDown, Users, Briefcase, DollarSign,
  BarChart3, Calendar, RefreshCw, Loader2, Award, CheckCircle, Target
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY || "localpros2024";

// Admin API axios config with authentication header
const adminApi = axios.create({
  baseURL: API,
  headers: {
    'X-Admin-Key': ADMIN_KEY
  }
});

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [period, setPeriod] = useState("30");
  const [adminKey, setAdminKey] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Analytics data states
  const [revenueData, setRevenueData] = useState(null);
  const [userGrowthData, setUserGrowthData] = useState(null);
  const [jobData, setJobData] = useState(null);
  const [contractorData, setContractorData] = useState(null);

  useEffect(() => {
    // Check session storage with timeout (8 hours)
    const savedAuth = sessionStorage.getItem("adminAuth");
    const authTime = sessionStorage.getItem("adminAuthTime");
    const SESSION_TIMEOUT = 8 * 60 * 60 * 1000; // 8 hours
    
    if (savedAuth === "true" && authTime) {
      const elapsed = Date.now() - parseInt(authTime, 10);
      if (elapsed < SESSION_TIMEOUT) {
        setIsAuthenticated(true);
      } else {
        sessionStorage.removeItem("adminAuth");
        sessionStorage.removeItem("adminAuthTime");
      }
    }
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [revenueRes, usersRes, jobsRes, contractorsRes] = await Promise.all([
        adminApi.get(`/admin/analytics/revenue?days=${period}`),
        adminApi.get(`/admin/analytics/users?days=${period}`),
        adminApi.get(`/admin/analytics/jobs?days=${period}`),
        adminApi.get(`/admin/analytics/contractors`)
      ]);
      
      setRevenueData(revenueRes.data);
      setUserGrowthData(usersRes.data);
      setJobData(jobsRes.data);
      setContractorData(contractorsRes.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, period]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics();
    setRefreshing(false);
    toast.success("Analytics refreshed");
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (adminKey === ADMIN_KEY) {
      setIsAuthenticated(true);
      sessionStorage.setItem("adminAuth", "true");
      sessionStorage.setItem("adminAuthTime", Date.now().toString());
    } else {
      toast.error("Invalid admin key");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
              <BarChart3 className="w-6 h-6 text-blue-600" />
            </div>
            <CardTitle>Analytics Dashboard</CardTitle>
            <CardDescription>Enter admin key to access analytics</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                placeholder="Admin Key"
                className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                data-testid="admin-key-input"
              />
              <Button type="submit" className="w-full" data-testid="login-btn">
                Access Analytics
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Analytics Dashboard</h1>
                <p className="text-sm text-slate-500">Revenue, Growth & Performance Metrics</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-32" data-testid="period-select">
                  <Calendar className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 Days</SelectItem>
                  <SelectItem value="14">14 Days</SelectItem>
                  <SelectItem value="30">30 Days</SelectItem>
                  <SelectItem value="60">60 Days</SelectItem>
                  <SelectItem value="90">90 Days</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={handleRefresh} disabled={refreshing} data-testid="refresh-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Revenue */}
          <Card data-testid="revenue-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Total Revenue</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {formatCurrency(revenueData?.total_revenue || 0)}
                  </p>
                  <div className="flex items-center mt-1">
                    {revenueData?.growth_rate >= 0 ? (
                      <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                    )}
                    <span className={`text-sm font-medium ${revenueData?.growth_rate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {revenueData?.growth_rate >= 0 ? '+' : ''}{revenueData?.growth_rate || 0}%
                    </span>
                    <span className="text-sm text-slate-400 ml-1">vs last period</span>
                  </div>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* New Contractors */}
          <Card data-testid="contractors-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">New Contractors</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {userGrowthData?.new_contractors || 0}
                  </p>
                  <div className="flex items-center mt-1">
                    {userGrowthData?.contractor_growth_rate >= 0 ? (
                      <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                    ) : (
                      <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                    )}
                    <span className={`text-sm font-medium ${userGrowthData?.contractor_growth_rate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {userGrowthData?.contractor_growth_rate >= 0 ? '+' : ''}{userGrowthData?.contractor_growth_rate || 0}%
                    </span>
                    <span className="text-sm text-slate-400 ml-1">growth</span>
                  </div>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* New Jobs */}
          <Card data-testid="jobs-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">New Jobs Posted</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {userGrowthData?.new_jobs || 0}
                  </p>
                  <div className="flex items-center mt-1">
                    <Briefcase className="w-4 h-4 text-slate-400 mr-1" />
                    <span className="text-sm text-slate-500">
                      {userGrowthData?.total_jobs || 0} total
                    </span>
                  </div>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Completion Rate */}
          <Card data-testid="completion-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Job Completion Rate</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {jobData?.completion_rate || 0}%
                  </p>
                  <div className="flex items-center mt-1">
                    <Target className="w-4 h-4 text-slate-400 mr-1" />
                    <span className="text-sm text-slate-500">
                      {jobData?.avg_applications_per_job || 0} avg applications
                    </span>
                  </div>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue Chart */}
          <Card data-testid="revenue-chart">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Revenue Trend
              </CardTitle>
              <CardDescription>Daily revenue over the selected period</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueData?.daily || []}>
                    <defs>
                      <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="_id" 
                      tickFormatter={formatDate}
                      tick={{ fontSize: 12 }}
                      stroke="#94a3b8"
                    />
                    <YAxis 
                      tickFormatter={(v) => `$${v}`}
                      tick={{ fontSize: 12 }}
                      stroke="#94a3b8"
                    />
                    <Tooltip 
                      formatter={(value) => [formatCurrency(value), 'Revenue']}
                      labelFormatter={formatDate}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      fill="url(#revenueGradient)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* User Growth Chart */}
          <Card data-testid="growth-chart">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                User & Job Growth
              </CardTitle>
              <CardDescription>New contractors and jobs posted daily</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={userGrowthData?.daily || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={formatDate}
                      tick={{ fontSize: 12 }}
                      stroke="#94a3b8"
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      stroke="#94a3b8"
                    />
                    <Tooltip 
                      labelFormatter={formatDate}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="contractors" 
                      name="Contractors"
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="jobs" 
                      name="Jobs"
                      stroke="#8b5cf6" 
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Revenue by Plan */}
          <Card data-testid="plan-revenue-chart">
            <CardHeader>
              <CardTitle>Revenue by Plan</CardTitle>
              <CardDescription>Distribution of revenue across plans</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={Object.entries(revenueData?.by_plan || {}).map(([key, value], index) => ({
                        name: key?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown',
                        value: value.revenue,
                        count: value.count
                      }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {Object.entries(revenueData?.by_plan || {}).map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value, name, props) => [
                        `${formatCurrency(value)} (${props.payload.count} sales)`,
                        name
                      ]}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Jobs by Category */}
          <Card data-testid="category-chart">
            <CardHeader>
              <CardTitle>Jobs by Category</CardTitle>
              <CardDescription>Distribution of jobs across services</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={(jobData?.by_category || []).slice(0, 6)} 
                    layout="vertical"
                    margin={{ left: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                    <YAxis 
                      dataKey="category" 
                      type="category" 
                      tick={{ fontSize: 11 }}
                      stroke="#94a3b8"
                      width={80}
                      tickFormatter={(v) => v?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || ''}
                    />
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    />
                    <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Job Status */}
          <Card data-testid="status-chart">
            <CardHeader>
              <CardTitle>Job Status</CardTitle>
              <CardDescription>Current status of all jobs</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={Object.entries(jobData?.by_status || {}).map(([key, value]) => ({
                        name: key?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown',
                        value: value
                      }))}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {Object.entries(jobData?.by_status || {}).map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Contractor Stats Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Subscription Breakdown */}
          <Card data-testid="subscription-chart">
            <CardHeader>
              <CardTitle>Contractor Subscriptions</CardTitle>
              <CardDescription>Breakdown by subscription plan</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(contractorData?.by_subscription || {}).map(([plan, count]) => {
                  const total = contractorData?.verification?.total || 1;
                  const percentage = Math.round((count / total) * 100);
                  const planName = plan === 'none' ? 'Free' : plan?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
                  
                  return (
                    <div key={plan}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-slate-700">{planName}</span>
                        <span className="text-slate-500">{count} ({percentage}%)</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div 
                          className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              
              <div className="mt-6 pt-6 border-t grid grid-cols-2 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{contractorData?.verification?.verified || 0}</p>
                  <p className="text-sm text-green-700">Verified</p>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{contractorData?.verification?.licensed || 0}</p>
                  <p className="text-sm text-blue-700">Licensed</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Performers */}
          <Card data-testid="top-performers">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-500" />
                Top Performers
              </CardTitle>
              <CardDescription>Highest rated contractors</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(contractorData?.top_performers || []).slice(0, 5).map((performer, index) => (
                  <div 
                    key={performer.id}
                    className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                      ${index === 0 ? 'bg-amber-100 text-amber-700' : 
                        index === 1 ? 'bg-slate-200 text-slate-700' : 
                        index === 2 ? 'bg-orange-100 text-orange-700' : 
                        'bg-slate-100 text-slate-600'}`}
                    >
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">
                        {performer.business_name || performer.name}
                      </p>
                      <p className="text-sm text-slate-500 truncate">
                        {performer.services?.join(', ')}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1">
                        <span className="text-amber-500">★</span>
                        <span className="font-semibold">{performer.rating?.toFixed(1)}</span>
                      </div>
                      <p className="text-xs text-slate-500">{performer.reviews_count} reviews</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Services Breakdown */}
        <Card data-testid="services-chart">
          <CardHeader>
            <CardTitle>Contractors by Service</CardTitle>
            <CardDescription>Number of contractors offering each service</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={contractorData?.by_service || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="service" 
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                    tickFormatter={(v) => v?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || ''}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                    formatter={(value) => [value, 'Contractors']}
                  />
                  <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AnalyticsDashboard;
